import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ShieldCheck } from 'lucide-react';
import { LicenseCard, ILicenseDateValue } from '../../components/LicenseCard';
import type { BillingLicenseKey, IBillingLicenseCatalogItem } from '../constants/billingLicense.constants';
import type { LicenseDateRange } from '../../../../utils/types/admin_types';

interface IBillingLicenseSectionProps {
  catalog: IBillingLicenseCatalogItem[];
  isLicenseActive: (key: BillingLicenseKey) => boolean;
  onToggleLicense: (key: BillingLicenseKey) => void;
  licenseStartDate: (key: BillingLicenseKey) => string | null;
  licenseEndDate: (key: BillingLicenseKey) => string | null;
  onLicenseDateChange: (key: BillingLicenseKey, field: keyof LicenseDateRange, value: ILicenseDateValue) => void;
  monthlyCreditsValue: (key: BillingLicenseKey) => string;
  onMonthlyCreditsChange: (key: BillingLicenseKey, value: string) => void;
}

export const BillingLicenseSection: React.FC<IBillingLicenseSectionProps> = ({
  catalog,
  isLicenseActive,
  onToggleLicense,
  licenseStartDate,
  licenseEndDate,
  onLicenseDateChange,
  monthlyCreditsValue,
  onMonthlyCreditsChange,
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
      <ShieldCheck size={22} color="#10b981" />
      <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
        License Configuration
      </Typography>
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        gap: 3,
      }}
    >
      {catalog.map((item) => (
        <LicenseCard
          key={item.id}
          title={item.label}
          iconPath={item.iconPath}
          iconClassName={item.iconClassName}
          isSpecialIcon={item.isSpecialIcon}
          active={isLicenseActive(item.licenseKey)}
          onToggle={() => onToggleLicense(item.licenseKey)}
          startDate={licenseStartDate(item.licenseKey)}
          endDate={licenseEndDate(item.licenseKey)}
          onStartDateChange={(value) => onLicenseDateChange(item.licenseKey, 'startDate', value)}
          onEndDateChange={(value) => onLicenseDateChange(item.licenseKey, 'endDate', value)}
          creditUnit={item.creditUnit}
          monthlyCredits={monthlyCreditsValue(item.licenseKey)}
          onMonthlyCreditsChange={(value) => onMonthlyCreditsChange(item.licenseKey, value)}
        />
      ))}
    </Box>
  </Paper>
);
